import { SaveClientFlags } from '@sdk/authentication/types';
import { AuthUserType } from '@sdk/authentication/types/AuthUserType';

import getUserRoleName from '@web/common/utils/getUserRoleName';

const FORM_URL =
  'https://script.google.com/macros/s/AKfycbxmkdxuNTX59rZTH34BDbq5GXSW_cAPPRAfdkU0S8jJ2W90P66rRP_NsCSGSoaZ5ZHQ4w/exec';
const SURVEY_FLAG_KEY = 'happinessSurveyDisplayed';
const BAD_RATING = 3;
const HAPPINESS_SURVEY_RELEASE_DATE = new Date('2023-10-15');

export function isBadScore(score: number): boolean {
  return score <= BAD_RATING;
}

type SurveyFormData = {
  user_id: string;
  user_name: string;
  email: string;
  role: string;
  org_id: string;
  org_name: string;
  org_creation: string;
  industry: string;
  user_count: string;
  location_count: string;
  toast_integration_status: string;
  score: string;
  comment: string;
};

type SurveyFeedback = {
  score: number;
  comment: string;
};

type AdditionalFeedbackData = {
  user: AuthUserType;
  saveClientFlags: SaveClientFlags;
  locationsCount: number;
  usersCount: number;
};

export async function sendSurveyFeedback(
  feedback: SurveyFeedback,
  additionalData: AdditionalFeedbackData,
  toastIntegrationStatus: string
): Promise<void> {
  const data = getFormData(feedback, additionalData, toastIntegrationStatus);
  const isValid = validateFeedback(feedback);

  if (isValid && !__DEV__) {
    fetch(`${FORM_URL}?${new URLSearchParams(data)}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
  }

  updateSurveyDisplayed(additionalData.saveClientFlags);
}

function getFormData(
  feedback: SurveyFeedback,
  additionalData: AdditionalFeedbackData,
  toastIntegrationStatus: string
): SurveyFormData {
  const { user, usersCount, locationsCount } = additionalData;
  const dismissedLabel = '**dismissed**';
  return {
    user_id: String(user.id),
    user_name: user.fullname,
    email: user.email,
    role: getUserRoleName(user.role),
    org_id: String(user.org.id),
    org_name: user.org.name,
    org_creation: String(user.org.createdAt).substr(0, 10),
    industry: user.org.businessCategory,
    user_count: String(usersCount),
    location_count: String(locationsCount),
    toast_integration_status: toastIntegrationStatus || undefined,
    score: String(feedback.score) || dismissedLabel,
    comment: feedback.comment || dismissedLabel,
  };
}

function validateFeedback(feedback: SurveyFeedback) {
  return typeof feedback.score === 'number';
}

export function updateSurveyDisplayed(saveClientFlags: SaveClientFlags) {
  saveClientFlags({ flags: { [SURVEY_FLAG_KEY]: new Date().toISOString() } });
}

function shouldDisplayByOrgExperience(user: AuthUserType) {
  return new Date(user.org.createdAt) > HAPPINESS_SURVEY_RELEASE_DATE && user.org.isOnTrial;
}

export function shouldCheckForHappinessSurvey(
  user: AuthUserType,
  areShiftsPublished,
  onScheduleTab
) {
  const wasDisplayed = !!user.settings.clientFlags[SURVEY_FLAG_KEY];
  return (
    !wasDisplayed &&
    user.isManager &&
    shouldDisplayByOrgExperience(user) &&
    areShiftsPublished &&
    onScheduleTab
  );
}
